/**
 * Grid Update Orchestrator
 * 
 * Manages all AG Grid API calls with proper timing, error handling, and logging.
 * Prevents race conditions and ensures grid operations don't interfere with each other.
 */

class GridUpdateOrchestrator {
  constructor(gridRef, options = {}) {
    this.gridRef = gridRef;
    this.pendingOperations = new Map();
    this.operationQueue = [];
    this.isProcessing = false;
    this.options = {
      defaultDelay: 0,
      maxRetries: 3,
      retryDelay: 100,
      enableLogging: options.enableLogging === true, // Disabled by default
      onError: options.onError || console.error,
      onWarning: options.onWarning || console.warn,
      ...options
    };
  }

  /**
   * Checks if grid API is available and ready
   */
  isGridReady() {
    return this.gridRef?.current?.api != null;
  }

  /**
   * Logs operation details if logging is enabled
   */
  log(level, operation, message, data = {}) {
    if (!this.options.enableLogging) return;

    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      operation,
      message,
      ...data
    };

    if (level === 'error') {
      this.options.onError(`[GridOrchestrator] ${operation}: ${message}`, logData);
    } else if (level === 'warn') {
      this.options.onWarning(`[GridOrchestrator] ${operation}: ${message}`, logData);
    } else {
      // console.log(`[GridOrchestrator] ${operation}: ${message}`, logData);
    }
  }

  /**
   * Executes a grid operation with retry logic and error handling
   */
  async executeOperation(operationName, operation, options = {}) {
    const {
      delay = this.options.defaultDelay,
      retries = this.options.maxRetries,
      priority = 'normal', // 'high', 'normal', 'low'
      skipIfBusy = false
    } = options;

    return new Promise((resolve, reject) => {
      const operationId = `${operationName}-${Date.now()}`;
      
      const execute = (attemptNumber = 1) => {
        setTimeout(() => {
          if (!this.isGridReady()) {
            if (attemptNumber < retries) {
              this.log('warn', operationName, `Grid not ready, retry ${attemptNumber}/${retries}`);
              execute(attemptNumber + 1);
            } else {
              const error = new Error('Grid API not available after retries');
              this.log('error', operationName, error.message);
              reject(error);
            }
            return;
          }

          try {
            this.log('info', operationName, `Executing (attempt ${attemptNumber})`);
            const result = operation(this.gridRef.current.api);
            this.log('info', operationName, 'Completed successfully');
            resolve(result);
          } catch (error) {
            if (attemptNumber < retries) {
              this.log('warn', operationName, `Failed, retry ${attemptNumber}/${retries}`, { error: error.message });
              setTimeout(() => execute(attemptNumber + 1), this.options.retryDelay);
            } else {
              this.log('error', operationName, 'Failed after all retries', { error: error.message });
              reject(error);
            }
          }
        }, delay);
      };

      execute();
    });
  }

  /**
   * Queues multiple operations to execute in sequence
   */
  async queueOperations(operations) {
    const results = [];
    
    for (const op of operations) {
      try {
        const result = await this.executeOperation(op.name, op.operation, op.options);
        results.push({ success: true, name: op.name, result });
      } catch (error) {
        results.push({ success: false, name: op.name, error });
        
        // Stop processing if a critical operation fails
        if (op.options?.critical) {
          this.log('error', 'queueOperations', `Critical operation ${op.name} failed, stopping queue`);
          break;
        }
      }
    }
    
    return results;
  }

  /**
   * Safely get a row node by ID
   */
  getRowNode(rowId, options = {}) {
    return this.executeOperation(
      `getRowNode:${rowId}`,
      (api) => api.getRowNode(rowId),
      { delay: 0, retries: 1, ...options }
    );
  }

  /**
   * Safely iterate over all nodes
   */
  forEachNode(callback, options = {}) {
    return this.executeOperation(
      'forEachNode',
      (api) => {
        const results = [];
        api.forEachNode((node) => {
          if (node.data) {
            results.push(callback(node));
          }
        });
        return results;
      },
      { delay: 0, ...options }
    );
  }

  /**
   * Get column definitions
   */
  getColumnDefs(options = {}) {
    return this.executeOperation(
      'getColumnDefs',
      (api) => api.getColumnDefs(),
      { delay: 0, retries: 1, ...options }
    );
  }

  /**
   * Get all columns
   */
  getColumns(options = {}) {
    return this.executeOperation(
      'getColumns',
      (api) => api.getColumns(),
      { delay: 0, retries: 1, ...options }
    );
  }

  /**
   * Auto-size all columns
   */
  autoSizeAllColumns(skipHeader = false, options = {}) {
    return this.executeOperation(
      'autoSizeAllColumns',
      (api) => api.autoSizeAllColumns(skipHeader),
      { delay: 0, ...options }
    );
  }

  /**
   * Add event listener
   */
  addEventListener(eventType, listener, options = {}) {
    return this.executeOperation(
      `addEventListener:${eventType}`,
      (api) => api.addEventListener(eventType, listener),
      { delay: 0, retries: 1, ...options }
    );
  }

  /**
   * Remove event listener
   */
  removeEventListener(eventType, listener, options = {}) {
    return this.executeOperation(
      `removeEventListener:${eventType}`,
      (api) => api.removeEventListener(eventType, listener),
      { delay: 0, retries: 1, ...options }
    );
  }

  /**
   * Common grid operations with built-in error handling
   */

  refreshCells(options = {}) {
    return this.executeOperation(
      'refreshCells',
      (api) => api.refreshCells({ force: true, ...options }),
      { delay: 0, ...options }
    );
  }

  setColumnDefs(columnDefs, options = {}) {
    return this.executeOperation(
      'setColumnDefs',
      (api) => api.setColumnDefs(columnDefs),
      { delay: 0, ...options }
    );
  }

  /**
   * Safely execute a grid operation by deferring to next tick
   * This ensures we're not calling grid API during rendering
   */
  async executeWhenReady(operationName, operation, options = {}) {
    const {
      timeout = 10000, // 10 second max wait
    } = options;

    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      const attemptOperation = () => {
        if (!this.isGridReady()) {
          if (Date.now() - startTime > timeout) {
            const error = new Error(`Grid not ready after ${timeout}ms timeout`);
            this.log('error', operationName, error.message);
            reject(error);
            return;
          }
          
          // Grid not ready, try again
          setTimeout(attemptOperation, 0);
          return;
        }

        // Grid is ready - defer execution to next tick to avoid render conflicts
        setTimeout(() => {
          try {
            this.log('info', operationName, 'Executing');
            const result = operation(this.gridRef.current.api);
            this.log('info', operationName, 'Completed successfully');
            resolve(result);
          } catch (error) {
            this.log('error', operationName, 'Failed', { error: error.message });
            reject(error);
          }
        }, 0);
      };

      // Start attempting
      attemptOperation();
    });
  }

  setGridOption(optionName, value, options = {}) {
    // Defer to next tick to avoid calling during render
    // This is AG Grid's recommended approach
    return this.executeWhenReady(
      `setGridOption:${optionName}`,
      (api) => api.setGridOption(optionName, value),
      options
    );
  }

  applyTransaction(transaction, options = {}) {
    return this.executeOperation(
      'applyTransaction',
      (api) => api.applyTransaction(transaction),
      { delay: 0, ...options }
    );
  }

  updateRowData(rowNode, newData, options = {}) {
    return this.executeOperation(
      'updateRowData',
      (api) => {
        const node = api.getRowNode(rowNode);
        if (node) {
          node.setData(newData);
          return true;
        }
        throw new Error(`Row node not found: ${rowNode}`);
      },
      { delay: 0, ...options }
    );
  }

  forEachNode(callback, options = {}) {
    return this.executeOperation(
      'forEachNode',
      (api) => {
        const results = [];
        api.forEachNode((node) => {
          if (node.data) {
            results.push(callback(node));
          }
        });
        return results;
      },
      { delay: 0, ...options }
    );
  }

  /**
   * Batch update multiple rows efficiently
   */
  async batchUpdateRows(updates, options = {}) {
    return this.executeOperation(
      'batchUpdateRows',
      (api) => {
        const updatedCount = updates.reduce((count, { rowId, data }) => {
          const node = api.getRowNode(rowId);
          if (node) {
            node.setData(data);
            return count + 1;
          }
          return count;
        }, 0);

        this.log('info', 'batchUpdateRows', `Updated ${updatedCount}/${updates.length} rows`);
        
        // Refresh cells after batch update
        api.refreshCells({ force: true });
        
        return { updatedCount, totalCount: updates.length };
      },
      { delay: 0, ...options }
    );
  }

  /**
   * Safely refresh the grid datasource
   */
  async refreshDatasource(datasource, options = {}) {
    return this.executeOperation(
      'refreshDatasource',
      (api) => api.setGridOption('serverSideDatasource', datasource),
      { delay: 100, ...options } // Longer delay for datasource refresh
    );
  }

  /**
   * Execute a complex multi-step grid update
   */
  async executeComplexUpdate(steps, options = {}) {
    const { rollbackOnError = false } = options;
    const completedSteps = [];
    
    try {
      for (const step of steps) {
        this.log('info', 'complexUpdate', `Executing step: ${step.name}`);
        
        const result = await this.executeOperation(
          step.name,
          step.operation,
          step.options || {}
        );
        
        completedSteps.push({ name: step.name, result });
      }
      
      this.log('info', 'complexUpdate', 'All steps completed successfully');
      return { success: true, steps: completedSteps };
      
    } catch (error) {
      this.log('error', 'complexUpdate', `Failed at step: ${completedSteps.length + 1}`, { error: error.message });
      
      if (rollbackOnError && steps[completedSteps.length].rollback) {
        this.log('info', 'complexUpdate', 'Attempting rollback');
        try {
          await this.executeOperation(
            'rollback',
            steps[completedSteps.length].rollback,
            {}
          );
          this.log('info', 'complexUpdate', 'Rollback successful');
        } catch (rollbackError) {
          this.log('error', 'complexUpdate', 'Rollback failed', { error: rollbackError.message });
        }
      }
      
      return { success: false, completedSteps, error };
    }
  }

  /**
   * Cancel all pending operations
   */
  cancelAll() {
    this.log('warn', 'cancelAll', `Cancelling ${this.pendingOperations.size} pending operations`);
    this.pendingOperations.clear();
    this.operationQueue = [];
  }

  /**
   * Get current status
   */
  getStatus() {
    return {
      isGridReady: this.isGridReady(),
      pendingOperations: this.pendingOperations.size,
      queuedOperations: this.operationQueue.length,
      isProcessing: this.isProcessing
    };
  }
}

export default GridUpdateOrchestrator;
